/* Click-led chapter navigation. Wheel, touch and keyboard scrolling stay native. */
(() => {
  const nav = document.querySelector('[data-kuzu-chapters]');
  const main = document.querySelector('main');
  if (!nav || !main || nav.dataset.ready) return;
  nav.dataset.ready = 'true';
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const list = nav.querySelector('[data-chapter-links]');
  const picker = nav.querySelector('[data-chapter-picker]');
  const trigger = nav.querySelector('[data-chapter-trigger]');
  const mobileList = nav.querySelector('[data-chapter-mobile-links]');
  const label = nav.querySelector('[data-chapter-label]');
  const previous = nav.querySelector('[data-chapter-prev]');
  const next = nav.querySelector('[data-chapter-next]');
  const count = nav.querySelector('[data-chapter-count]');
  const definitions = [
    ['.kuzu-signature', 'The tincture', 'kuzu-introduction'],
    ['#kuzu-444', '444 Edition'],
    ['[data-kuzu-sacred]', 'The Sacred Body'],
    ['#kuzu-shop', 'Your ritual'],
    ['#kuzu-science', 'Science'],
    ['#kuzu-origin', 'Origin'],
    ['#trace-your-origin', 'Trace a batch'],
    ['#kuzu-journal', 'Stories']
  ];
  let chapters = [], active = -1, flight = null, frame = 0;

  const offset = () => {
    const header = document.querySelector('.kuzu-header');
    const rect = header?.getBoundingClientRect();
    // Some header groups scroll out of view; never reserve their height off-screen.
    return rect && rect.top <= 0 && rect.bottom > 0 ? rect.bottom + 16 : 16;
  };
  const destination = (target) => Math.max(0, Math.min(
    window.scrollY + target.getBoundingClientRect().top - offset(),
    document.documentElement.scrollHeight - window.innerHeight
  ));
  const setActive = (index) => {
    if (index === active || index < 0) return;
    active = index;
    chapters.forEach((chapter, i) => {
      [chapter.link, chapter.mobileLink].forEach(link => {
        if (i === index) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    });
    label.textContent = chapters[index].label;
    count.textContent = `${index + 1} / ${chapters.length}`;
    previous.disabled = index === 0;
    next.disabled = index === chapters.length - 1;
    previous.setAttribute('aria-label', index > 0 ? `Previous: ${chapters[index - 1].label}` : 'First chapter');
    next.setAttribute('aria-label', index < chapters.length - 1 ? `Next: ${chapters[index + 1].label}` : 'Last chapter');
  };
  const track = () => {
    frame = 0;
    if (flight || !chapters.length) return;
    const line = offset() + window.innerHeight * .22;
    let index = 0;
    chapters.forEach((chapter, i) => { if (chapter.target.getBoundingClientRect().top <= line) index = i; });
    setActive(index);
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(track); };
  const cancel = () => {
    if (!flight) return;
    flight.cancelled = true;
    cancelAnimationFrame(flight.frame);
    flight.animation?.cancel();
    flight = null;
    document.documentElement.classList.remove('kuzu-chapter-travelling');
    schedule();
  };
  const expose = (target) => {
    // Coordinate with the existing reveal layer before jumping past unvisited sections.
    const section = target.closest('.shopify-section');
    if (section) { section.dataset.kuzuVisible = ''; section.dataset.kuzuInstant = ''; }
  };
  const go = async (target, { history = true, focus = true, instant = false } = {}) => {
    if (picker.open) { picker.open = false; trigger.focus({ preventScroll: true }); }
    cancel();
    const journey = { cancelled: false, frame: 0, animation: null };
    flight = journey;
    expose(target);
    document.querySelectorAll('.kuzu-mobile-nav[open]').forEach(menu => { menu.open = false; });
    document.documentElement.classList.add('kuzu-chapter-travelling');
    const start = window.scrollY, end = destination(target), distance = Math.abs(end - start);
    const index = chapters.findIndex(chapter => chapter.target === target);
    setActive(index);
    const finish = () => {
      if (journey.cancelled) return;
      window.scrollTo({ top: destination(target), behavior: 'instant' });
      if (history && window.location.hash !== `#${target.id}`) {
        const url = new URL(window.location.href);
        url.hash = target.id;
        window.history.pushState(window.history.state, '', url);
      }
      if (focus) {
        const heading = target.querySelector('h1, h2') || target;
        const temporary = !heading.hasAttribute('tabindex');
        if (temporary) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
        if (temporary) heading.addEventListener('blur', () => heading.removeAttribute('tabindex'), { once: true });
      }
      flight = null;
      document.documentElement.classList.remove('kuzu-chapter-travelling');
      schedule();
    };
    if (instant || preference.matches || window.Shopify?.designMode || distance < 4) { finish(); return; }
    // Distant chapters dissolve directly; adjacent chapters glide through the page.
    // No screen overlay, looping effect, dependency or wheel/touch interception.
    if (distance > window.innerHeight * 1.75 && main.animate) {
      try {
        journey.animation = main.animate([{ opacity: 1 }, { opacity: .12 }], { duration: 140, fill: 'forwards', easing: 'ease-out' });
        await journey.animation.finished;
        if (journey.cancelled) return;
        window.scrollTo({ top: destination(target), behavior: 'instant' });
        journey.animation.cancel();
        journey.animation = main.animate([{ opacity: .12 }, { opacity: 1 }], { duration: 340, easing: 'cubic-bezier(.22,1,.36,1)' });
        await journey.animation.finished;
      } catch (_) {
        if (journey.cancelled) return;
      }
      finish();
    } else {
      const duration = Math.min(850, Math.max(480, distance * .35));
      let began;
      const step = (time) => {
        if (journey.cancelled) return;
        began ??= time;
        const progress = Math.min(1, (time - began) / duration);
        const eased = progress < .5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
        window.scrollTo({ top: start + (end - start) * eased, behavior: 'instant' });
        if (progress < 1) journey.frame = requestAnimationFrame(step);
        else finish();
      };
      journey.frame = requestAnimationFrame(step);
    }
  };
  const build = () => {
    cancel();
    picker.open = false;
    list.replaceChildren(); mobileList.replaceChildren();
    chapters = definitions.flatMap(([selector, label, fallback]) => {
      const target = main.querySelector(selector);
      if (!target) return [];
      if (!target.id) target.id = fallback;
      target.dataset.kuzuChapterTarget = '';
      return [{ target, label }];
    }).sort((a, b) => a.target.compareDocumentPosition(b.target) & 2 ? 1 : -1);
    chapters.forEach((chapter, index) => {
      const li = document.createElement('li'), link = document.createElement('a');
      link.href = `#${chapter.target.id}`; link.textContent = chapter.label;
      const mobileItem = document.createElement('li'), mobileLink = document.createElement('a');
      mobileLink.href = link.href; mobileLink.textContent = chapter.label;
      mobileLink.dataset.number = String(index + 1).padStart(2, '0');
      mobileItem.append(mobileLink); mobileList.append(mobileItem);
      li.append(link); list.append(li); chapter.link = link; chapter.mobileLink = mobileLink;
    });
    active = -1;
    nav.hidden = chapters.length < 2;
    document.documentElement.classList.toggle('kuzu-chapters-enabled', !nav.hidden);
    track();
  };
  document.addEventListener('click', (event) => {
    if (picker.open && !picker.contains(event.target)) picker.open = false;
    const link = event.target.closest('a[href]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || (url.search && url.search !== window.location.search) || !url.hash) return;
    let id;
    try { id = decodeURIComponent(url.hash.slice(1)); } catch (_) { return; }
    const chapter = chapters.find(item => item.target.id === id);
    if (!chapter) return;
    event.preventDefault();
    go(chapter.target, { focus: !nav.contains(link) });
  });
  picker.addEventListener('keydown', event => {
    if (event.key === 'Escape' && picker.open) {
      event.preventDefault(); picker.open = false; trigger.focus({ preventScroll: true });
    }
  });
  picker.addEventListener('focusout', event => {
    if (event.relatedTarget && !picker.contains(event.relatedTarget)) picker.open = false;
  });
  previous.addEventListener('click', () => { if (active > 0) go(chapters[active - 1].target, { focus: false }); });
  next.addEventListener('click', () => { if (active < chapters.length - 1) go(chapters[active + 1].target, { focus: false }); });
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', () => { picker.open = false; cancel(); schedule(); });
  window.addEventListener('wheel', cancel, { passive: true });
  window.addEventListener('touchstart', cancel, { passive: true });
  document.addEventListener('keydown', event => { if (['Escape', 'Tab', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) cancel(); });
  preference.addEventListener?.('change', cancel);
  const followHash = () => {
    cancel();
    let id;
    try { id = decodeURIComponent(window.location.hash.slice(1)); } catch (_) { return; }
    const chapter = chapters.find(item => item.target.id === id);
    if (chapter) go(chapter.target, { history: false, focus: false, instant: true });
  };
  window.addEventListener('hashchange', followHash);
  window.addEventListener('popstate', followHash);
  document.addEventListener('shopify:section:load', build);
  document.addEventListener('shopify:section:reorder', build);
  document.addEventListener('shopify:section:unload', () => { cancel(); requestAnimationFrame(build); });
  document.addEventListener('shopify:section:select', cancel);
  build();
  if (document.readyState === 'complete') followHash();
  else window.addEventListener('load', followHash, { once: true });
})();
